import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, DataTable, Details, Dialog, IconButton, LogStream, MetricTile,
  StatusChip, Tabs, TextField, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, type Instance } from '../lib/api';
import { ISTANZA_IN_CORSO } from '../lib/dati';
import { useTail } from '../lib/sse';
import { durata, quandoRelativo, titolo } from '../lib/attivita';
import { ETICHETTA, TONO_ISTANZA } from './instances';
import { ETICHETTA as ETICHETTA_JOB, TONO as TONO_JOB } from './jobs';
import { ESITO, peso } from './backup-copertura';
import { Domini } from './domini';
import { Addon, useRepoCollegati } from './addons';
import { usePermessiSu } from '../session';
import { ConPermesso } from '../permesso';
import { LibreriePython, ModuliOdoo } from './moduli';
import { ApplicaBlueprint } from './blueprint';
import { ChipCopia, FasciaCopia, Staging } from './staging';
import { EntraInOdoo } from './entra';
import { Anello, CaricoSezione, Logo } from '../brand';
import { Back, History, Play, Refresh, Stop, Trash, Tune, Warn } from '../icons';
import { localeIntl, t } from '../lib/lingua';

/**
 * La scheda di un'istanza: cosa sta facendo, se risponde, dove risponde, con
 * quante risorse, quando è stata salvata l'ultima volta e cosa le è successo.
 *
 * Le azioni sono in alto, dove si cerca un interruttore: avvia, ferma,
 * riavvia. Sono job brevi — pochi secondi — quindi non si va al log, si resta
 * qui a vedere lo stato cambiare. La riconfigurazione invece dura un minuto
 * e ha i suoi passi: quella porta alla schermata dell'attività.
 */

/** Cosa dice il molo mentre l'hub lavora sull'istanza. */
const IN_CORSO: Record<string, string> = {
  creating: 'La sto creando: ci vogliono un paio di minuti.',
  starting: 'La sto avviando.',
  stopping: 'La sto fermando.',
  restarting: 'La sto riavviando.',
  modifying: 'Sto riscrivendo la configurazione e riavviando.',
  restoring: 'Sto riportando i dati del backup. Prima ne faccio una copia di sicurezza, poi li sostituisco e riavvio.',
  deleting: 'La sto rimuovendo dal server.',
};

export function InstancePage() {
  const { slug, id } = useParams({ from: '/shell/orgs/$slug/instances/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [modifica, setModifica] = useState(false);
  const [elimina, setElimina] = useState(false);
  const permessi = usePermessiSu(slug, { istanza: id });
  // Con dei repository, riconfigurare li porta alla punta del ramo.
  const repo = useRepoCollegati(slug, id);
  const perRiconfigurare = (repo.data?.length ?? 0) > 0
    ? ['instance.config.write', 'instance.code.pull'] : 'instance.config.write';

  const q = useQuery({
    queryKey: ['instance', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/instances/{id}', { params: { path: { slug, id } } })),
    // Mentre l'hub lavora si aggiorna ogni pochi secondi; a riposo, ogni
    // minuto, che è la cadenza con cui l'agent riporta la salute.
    refetchInterval: (query) => (ISTANZA_IN_CORSO.has(query.state.data?.status ?? '') ? 3000 : 60_000),
  });
  const istanza = q.data;

  const jobs = useQuery({
    queryKey: ['instance-jobs', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/jobs', { params: { path: { slug }, query: { instanceId: id, limit: 10 } } })),
    refetchInterval: (query) =>
      (query.state.data?.jobs ?? []).some((j) => j.status === 'running' || j.status === 'queued') ? 3000 : false,
  });
  const backups = useQuery({
    queryKey: ['backups', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/instances/{id}/backups', { params: { path: { slug, id }, query: { limit: 5 } } })),
  });

  const invalida = () => {
    qc.invalidateQueries({ queryKey: ['instance', slug, id] });
    qc.invalidateQueries({ queryKey: ['instances', slug] });
    qc.invalidateQueries({ queryKey: ['instance-jobs', slug, id] });
  };

  // Le tre operazioni sull'unit: si accodano e si resta qui a guardare.
  const dopo = (frase: string) => ({
    onSuccess: () => {
      invalida();
      avvisa(frase);
    },
    onError: (e: Error) => avvisa(e.message),
  });
  const nome = istanza?.name ?? t('l’istanza');
  const avvia = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs/{slug}/instances/{id}/start', { params: { path: { slug, id } } })),
    ...dopo(t('Sto avviando {nome}', { nome })),
  });
  const ferma = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs/{slug}/instances/{id}/stop', { params: { path: { slug, id } } })),
    ...dopo(t('Sto fermando {nome}', { nome })),
  });
  const riavvia = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs/{slug}/instances/{id}/restart', { params: { path: { slug, id } } })),
    ...dopo(t('Sto riavviando {nome}', { nome })),
  });

  const rigenera = useMutation({
    mutationFn: (body: { workers?: number; memoryMb?: number; timeoutSeconds?: number }) =>
      must(api.PATCH('/api/v1/orgs/{slug}/instances/{id}', { params: { path: { slug, id } }, body })),
    onSuccess: (inst) => {
      invalida();
      setModifica(false);
      avvisa(t('Riscrivo la configurazione: ci vuole un minuto'));
      if (inst.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: inst.jobId } });
    },
  });

  const backupAdesso = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs/{slug}/instances/{id}/backups', { params: { path: { slug, id } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backups', slug, id] });
      invalida();
      avvisa(t('Backup avviato'));
    },
    onError: (e: Error) => avvisa(e.message),
  });

  const rimuovi = useMutation({
    mutationFn: () => must(api.DELETE('/api/v1/orgs/{slug}/instances/{id}', { params: { path: { slug, id } } })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instances', slug] });
      avvisa(t('Sto rimuovendo {nome}', { nome: istanza?.name ?? t('l’istanza') }));
      navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  const inCorso = istanza ? ISTANZA_IN_CORSO.has(istanza.status) : false;
  const ultimoJob = jobs.data?.jobs?.[0];
  const vaiAlJob = (jobId: string) => navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: jobId } });
  const occupata = inCorso || avvia.isPending || ferma.isPending || riavvia.isPending;

  return (
    <>
      <TopAppBar
        leading={
          <IconButton label={t('Torna alle istanze')} onClick={() => navigate({ to: '/orgs/$slug/instances', params: { slug } })}>
            <Back />
          </IconButton>
        }
        title={istanza?.name ?? '…'}
        subtitle={istanza ? `${istanza.serverName ?? 'server'} · Odoo ${istanza.odooMajor}` : undefined}
        actions={
          istanza && (
            <div className="md-stack__inline" style={{ alignItems: 'center' }}>
              {istanza.staging && <ChipCopia staging={istanza.staging} />}
              <StatusChip status={istanza.status} label={t(ETICHETTA[istanza.status] ?? istanza.status)} tone={TONO_ISTANZA[istanza.status]} />
              {(istanza.status === 'stopped' || istanza.status === 'error') && (
                <ConPermesso ha={permessi} serve="instance.start">
                  <Button variant="filled" icon={<Play />} disabled={occupata} onClick={() => avvia.mutate()}>{t('Avvia')}</Button>
                </ConPermesso>
              )}
              {(istanza.status === 'running' || istanza.status === 'error') && (
                <>
                  <ConPermesso ha={permessi} serve="instance.restart">
                    <Button variant="tonal" icon={<Refresh />} disabled={occupata} onClick={() => riavvia.mutate()}>{t('Riavvia')}</Button>
                  </ConPermesso>
                  <ConPermesso ha={permessi} serve="instance.stop">
                    <Button variant="text" icon={<Stop />} disabled={occupata} onClick={() => ferma.mutate()}>{t('Ferma|azione')}</Button>
                  </ConPermesso>
                </>
              )}
            </div>
          )
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        {q.error && <Banner tone="error" title={t('Istanza non leggibile')}>{q.error.message}</Banner>}

        {istanza && (
          <div className="md-stack">
            <FasciaCopia slug={slug} istanza={istanza} />

            {inCorso && (
              <Card variant="outlined">
                <div className="md-attesa">
                  <Logo size={56} anim="livello" label={t('In corso')} />
                  <p className="md-body-medium">{t(IN_CORSO[istanza.status] ?? 'Ci sto lavorando.')}</p>
                  {ultimoJob && (ultimoJob.status === 'running' || ultimoJob.status === 'queued') && (
                    <Button variant="tonal" onClick={() => vaiAlJob(ultimoJob.id)}>{t('Segui')}</Button>
                  )}
                </div>
              </Card>
            )}

            {istanza.status === 'error' && (
              <Banner
                tone="error"
                icon={<Warn />}
                title={t('L’ultima operazione non è riuscita')}
                action={
                  <div className="md-stack__inline">
                    {ultimoJob && <Button variant="outlined" onClick={() => vaiAlJob(ultimoJob.id)}>{t('Cosa è successo')}</Button>}
                    {/* Qui e non solo nei dettagli tecnici: un'istanza rotta è
                        il caso in cui si cerca il modo di toglierla, e un
                        bottone nascosto sotto un espansore non si trova. */}
                    <ConPermesso ha={permessi} serve="instance.delete">
                      <Button variant="text" icon={<Trash />} onClick={() => setElimina(true)}>{t('Rimuovi')}</Button>
                    </ConPermesso>
                  </div>
                }
              >
                {ultimoJob?.error || t('Guarda l’ultima attività per capire dove si è fermata. Puoi riavviarla, rigenerare la configurazione, o toglierla.')}
              </Banner>
            )}

            <Salute istanza={istanza} />

            <Card variant="outlined">
              <div className="md-section-head">
                <h2 className="md-title-medium">{t('Indirizzi')}</h2>
                <EntraInOdoo slug={slug} istanza={istanza} />
              </div>
              <Domini slug={slug} istanza={istanza} />
            </Card>

            <Card variant="outlined">
              <div className="md-section-head">
                <h2 className="md-title-medium">{t('Moduli aggiuntivi')}</h2>
                <span className="md-stack__inline">
                  <ApplicaBlueprint slug={slug} istanza={istanza} />
                  <ModuliOdoo slug={slug} istanza={istanza} />
                </span>
              </div>
              <Addon slug={slug} istanza={istanza} />
            </Card>

            <Card variant="outlined">
              <h2 className="md-title-medium">{t('Librerie Python')}</h2>
              <LibreriePython key={(istanza.pythonPackages ?? []).join(' ')} slug={slug} istanza={istanza} />
            </Card>

            {!istanza.staging && (
              <Card variant="outlined">
                <h2 className="md-title-medium">{t('Copie di prova')}</h2>
                <Staging slug={slug} istanza={istanza} />
              </Card>
            )}

            <Card variant="outlined">
              <div className="md-section-head">
                <h2 className="md-title-medium">{t('Risorse')}</h2>
                <div className="md-stack__inline">
                  <ConPermesso ha={permessi} serve={perRiconfigurare}>
                    <Button variant="text" disabled={inCorso || istanza.status === 'stopped'} onClick={() => rigenera.mutate({})}>
                      {t('Rigenera la configurazione')}
                    </Button>
                  </ConPermesso>
                  <ConPermesso ha={permessi} serve={perRiconfigurare}>
                    <Button variant="tonal" icon={<Tune />} disabled={inCorso || istanza.status === 'stopped'} onClick={() => setModifica(true)}>
                      {t('Modifica')}
                    </Button>
                  </ConPermesso>
                </div>
              </div>
              <div className="md-metric-grid">
                <MetricTile label={t('Worker')} value={istanza.workers} unit={` ${t('processi')}`} />
                <MetricTile
                  label={t('Memoria per worker')}
                  value={(istanza.memoryMb / 1024).toLocaleString(localeIntl(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                  unit=" GB"
                />
                <MetricTile label={t('Richiesta più lunga')} value={durata(istanza.timeoutSeconds)} />
              </div>
              <p className="md-body-small" style={{ margin: 'var(--md-sys-spacing-3) 0 0', color: 'var(--md-sys-color-on-surface-variant)' }}>
                {t('Rigenerare riporta l’istanza in linea con quello che c’è scritto qui: aggiorna i moduli dai repository collegati, riscrive configurazione, servizio e indirizzo web dagli stessi passi della creazione, e riavvia. Serve anche a portare in HTTPS un’istanza nata prima del certificato del server.')}
                {rigenera.error ? ` ${rigenera.error.message}` : ''}
              </p>
            </Card>

            {/* Le copie non si salvano: si rifanno dalla produzione, e la
                copertura dei backup non le conta. */}
            {!istanza.staging && (
              <Card variant="outlined">
                <div className="md-section-head">
                  <h2 className="md-title-medium">{t('Backup')}</h2>
                  <div className="md-stack__inline">
                    <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/backup', params: { slug }, search: { istanza: id } })}>
                      {t('Tutti i backup')}
                    </Button>
                    <ConPermesso ha={permessi} serve="backup.create">
                      <Button variant="tonal" icon={backupAdesso.isPending ? <Anello /> : undefined} disabled={backupAdesso.isPending || istanza.status !== 'running'} onClick={() => backupAdesso.mutate()}>
                        {t('Backup adesso')}
                      </Button>
                    </ConPermesso>
                  </div>
                </div>
                {backups.isSuccess && (backups.data.backups ?? []).length === 0 && (
                  <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                    {t('Nessun backup ancora. Dalla pagina dei backup si pianificano quelli automatici.')}
                  </p>
                )}
                {(backups.data?.backups ?? []).length > 0 && (
                  <DataTable
                    columns={[
                      { key: 'quando', header: t('Quando'), nowrap: true },
                      { key: 'esito', header: t('Esito') },
                      { key: 'peso', header: t('Dimensione'), numeric: true },
                    ]}
                    rowKey={(r) => String(r.id)}
                    rows={(backups.data?.backups ?? []).map((b) => {
                      const e = ESITO[b.status] ?? { status: b.status, label: b.status };
                      return {
                        id: b.id,
                        quando: quandoRelativo(b.startedAt),
                        esito: <StatusChip status={e.status} label={t(e.label)} />,
                        peso: peso(b.sizeBytes),
                      };
                    })}
                  />
                )}
              </Card>
            )}

            <Card variant="outlined">
              <h2 className="md-title-medium">{t('Attività recenti')}</h2>
              {jobs.isSuccess && (jobs.data.jobs ?? []).length === 0 && (
                <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>{t('Ancora niente.')}</p>
              )}
              {(jobs.data?.jobs ?? []).length > 0 && (
                <DataTable
                  columns={[
                    { key: 'cosa', header: t('Attività') },
                    { key: 'stato', header: t('Stato') },
                    { key: 'quando', header: t('Avviata'), nowrap: true },
                  ]}
                  rowKey={(r) => String(r.id)}
                  onRowClick={(r) => vaiAlJob(String(r.id))}
                  rows={(jobs.data?.jobs ?? []).map((j) => ({
                    id: j.id,
                    cosa: (
                      <div className="md-due-righe">
                        <span className="md-body-large">{titolo(j.kind, istanza.name)}</span>
                        {j.status === 'failed' && j.error && <span className="md-clamp">{j.error}</span>}
                      </div>
                    ),
                    stato: <StatusChip status={j.status} label={t(ETICHETTA_JOB[j.status] ?? j.status)} tone={TONO_JOB[j.status]} />,
                    quando: quandoRelativo(j.startedAt ?? j.createdAt),
                  }))}
                />
              )}
            </Card>

            <DettagliTecnici slug={slug} istanza={istanza} onElimina={() => setElimina(true)} />
          </div>
        )}
      </div>

      {istanza && (
        <ModificaRisorse
          open={modifica}
          istanza={istanza}
          inCorso={rigenera.isPending}
          errore={rigenera.error?.message}
          onOpenChange={setModifica}
          onConferma={(body) => rigenera.mutate(body)}
        />
      )}

      <Dialog
        open={elimina}
        onOpenChange={setElimina}
        title={t('Rimuovere {nome}?', { nome: istanza?.name ?? t('questa istanza') })}
        description={t('Se ne vanno il servizio, il database, gli allegati e l’indirizzo web. I backup già fatti restano nella destinazione. Non si torna indietro.')}
        actions={
          <>
            <Button variant="text" onClick={() => setElimina(false)}>{t('Annulla')}</Button>
            <Button variant="danger" icon={rimuovi.isPending ? <Anello /> : undefined} disabled={rimuovi.isPending} onClick={() => rimuovi.mutate()}>
              {rimuovi.isPending ? t('Rimuovo…') : t('Rimuovi')}
            </Button>
          </>
        }
      >
        {rimuovi.error && <Banner tone="error" title={t('Non rimossa')}>{rimuovi.error.message}</Banner>}
      </Dialog>
    </>
  );
}

/**
 * Come sta, con le parole di chi guarda: «Risponde», «Non risponde da tre
 * minuti», «Ferma». Il codice HTTP e i riavvii stanno nei dettagli tecnici.
 */
function Salute({ istanza }: { istanza: Instance }) {
  const h = istanza.health;
  if (istanza.status === 'stopped') {
    return (
      <Card variant="outlined">
        <div className="md-section-head">
          <h2 className="md-title-medium">{t('Ferma')}</h2>
          <StatusChip status="stopped" label={t('Ferma')} tone="neutral" />
        </div>
        <p className="md-body-medium" style={{ margin: 0 }}>{t('L’istanza è spenta: non risponde e non consuma. Si riaccende da qui sopra.')}</p>
      </Card>
    );
  }
  if (!h) {
    return (
      <Banner icon={<Logo size={24} anim="marea" tone="inherit" />} title={t('Ancora nessun dato sull’istanza')}>
        {t('L’agente del server la interroga ogni minuto: il primo campione arriva a breve.')}
      </Banner>
    );
  }
  const risponde = h.http === 200;
  const titoloSalute = risponde
    ? t('Risponde')
    : h.okAt ? t('Non risponde da {quando}', { quando: quandoRelativo(h.okAt) }) : t('Non risponde');
  return (
    // Quando va bene la scheda intera lo dice, in success-container, con
    // l'onda del marchio in filigrana: è la prima cosa che si guarda.
    <Card variant="outlined" className={risponde ? 'md-card--ok' : undefined}>
      {risponde && (
        <svg className="md-card__onda" viewBox="0 0 200 72" aria-hidden="true">
          <path d="M0 46c25-22 50-22 75 0s50 22 75 0 37-16 50 0" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
        </svg>
      )}
      <div className="md-section-head">
        <h2 className={risponde ? 'md-title-large' : 'md-title-medium'}>{titoloSalute}</h2>
        {/* Quando va bene lo dice già la scheda intera: il chip lo si tiene per il caso brutto. */}
        {!risponde && <StatusChip status="error" label={t('Non risponde')} tone="error" />}
      </div>
      {/* Sono fatti, non misure: un riquadro da metrica a cifre grandi con
          dentro «Raggiungibile» trabocca sul telefono e stona sul desktop. */}
      <dl className="md-dati">
        <div className="md-dato"><dt>Database</dt><dd>{risponde ? (h.dbOk ? t('Raggiungibile') : t('Non raggiungibile')) : '—'}</dd></div>
        <div className="md-dato"><dt>{t('Riavvii del servizio')}</dt><dd>{h.restarts > 0 ? t('{n} da quando è acceso', { n: h.restarts }) : h.restarts}</dd></div>
        <div className="md-dato"><dt>{t('Ultimo controllo')}</dt><dd>{quandoRelativo(h.checkedAt)}</dd></div>
      </dl>
      {risponde && !h.dbOk && (
        <p className="md-body-small" style={{ margin: 'var(--md-sys-spacing-3) 0 0', color: 'var(--md-sys-color-error)' }}>
          {t('Odoo risponde ma non raggiunge il suo database: le pagine falliranno.')}
        </p>
      )}
      {h.restarts >= 3 && (
        <p className="md-body-small" style={{ margin: 'var(--md-sys-spacing-3) 0 0', color: 'var(--md-sys-color-error)' }}>
          {t('Il servizio si è riavviato da solo più volte: di solito è la memoria. Guarda il log qui sotto.')}
        </p>
      )}
    </Card>
  );
}

/** Tre campi, quindi un dialogo. Gli zeri non si mandano: l'hub lascia com'è. */
function ModificaRisorse({
  open, istanza, inCorso, errore, onOpenChange, onConferma,
}: {
  open: boolean; istanza: Instance; inCorso: boolean; errore?: string;
  onOpenChange: (v: boolean) => void;
  onConferma: (body: { workers?: number; memoryMb?: number; timeoutSeconds?: number }) => void;
}) {
  const [workers, setWorkers] = useState(String(istanza.workers));
  const [memoria, setMemoria] = useState(String(istanza.memoryMb));
  const [timeout, setTimeout_] = useState(String(istanza.timeoutSeconds));
  const w = Number(workers); const m = Number(memoria); const sec = Number(timeout);
  const ok = w >= 1 && w <= 32 && m >= 512 && m <= 65536 && sec >= 30 && sec <= 3600;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Risorse dell’istanza')}
      description={t('L’hub riscrive la configurazione e riavvia Odoo: chi sta lavorando perde la sessione per qualche secondo.')}
      actions={
        <>
          <Button variant="text" onClick={() => onOpenChange(false)}>{t('Annulla')}</Button>
          <Button variant="filled" icon={inCorso ? <Anello /> : undefined} disabled={!ok || inCorso} onClick={() => onConferma({ workers: w, memoryMb: m, timeoutSeconds: sec })}>
            {inCorso ? t('Applico…') : t('Applica e riavvia')}
          </Button>
        </>
      }
    >
      <form className="md-form" onSubmit={(e) => { e.preventDefault(); if (ok && !inCorso) onConferma({ workers: w, memoryMb: m, timeoutSeconds: sec }); }}>
        {errore && <Banner tone="error" title={t('Non applicato')}>{errore}</Banner>}
        <TextField label={t('Worker')} type="number" min={1} max={32} value={workers} onChange={(e) => setWorkers(e.target.value)}
          help={t('Processi che servono le pagine. Ognuno tiene occupata la sua memoria: su un server condiviso, pochi.')} />
        <TextField label={t('Memoria per worker (MB)')} type="number" min={512} max={65536} step={256} value={memoria} onChange={(e) => setMemoria(e.target.value)}
          help={t('Oltre questo limite il worker viene riciclato. 2048 è il valore con cui nasce un’istanza.')} />
        <TextField label={t('Richiesta più lunga (secondi)')} type="number" min={30} max={3600} value={timeout} onChange={(e) => setTimeout_(e.target.value)}
          help={t('Una richiesta che dura di più viene interrotta. Report pesanti e importazioni ne vogliono di più.')} />
      </form>
    </Dialog>
  );
}

/**
 * Il resto: identificativi, porte, e il log in tempo reale. Il log si apre
 * solo con i dettagli aperti, perché dall'altra parte c'è un `tail` via SSH
 * che vive quanto la connessione.
 */
function DettagliTecnici({ slug, istanza, onElimina }: { slug: string; istanza: Instance; onElimina: () => void }) {
  const navigate = useNavigate();
  const [aperto, setAperto] = useState(false);
  const [sorgente, setSorgente] = useState<'odoo' | 'nginx'>('odoo');
  const url = aperto ? `/api/v1/orgs/${slug}/instances/${istanza.id}/logs?source=${sorgente}` : null;
  const log = useTail(url);
  const permessi = usePermessiSu(slug, { istanza: istanza.id });

  return (
    <Details summary={t('Dettagli tecnici')} onToggle={(e) => setAperto((e.currentTarget as HTMLDetailsElement).open)}>
      <dl className="md-dati">
        <div className="md-dato"><dt>{t('Identificativo')}</dt><dd className="md-mono">{istanza.id}</dd></div>
        <div className="md-dato"><dt>Database</dt><dd className="md-mono">{istanza.dbName}</dd></div>
        <div className="md-dato"><dt>Unit</dt><dd className="md-mono">{istanza.unitName || `odoo@${istanza.name}`}</dd></div>
        <div className="md-dato"><dt>{t('Porte')}</dt><dd className="md-mono">{istanza.httpPort} · {istanza.geventPort}</dd></div>
        <div className="md-dato"><dt>Server</dt><dd><a href="#" onClick={(e) => { e.preventDefault(); navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: istanza.serverId } }); }}>{istanza.serverName}</a></dd></div>
        <div className="md-dato"><dt>{t('Creata')}</dt><dd>{new Date(istanza.createdAt).toLocaleString(localeIntl())}</dd></div>
        {istanza.health && <div className="md-dato"><dt>/web/health</dt><dd className="md-mono">{istanza.health.http || t('nessuna risposta')}</dd></div>}
      </dl>
      <div className="md-stack__inline md-sezione">
        <Button variant="text" icon={<History />} onClick={() => navigate({ to: '/orgs/$slug/registro', params: { slug }, search: { risorsa: 'instance', id: istanza.id } })}>
          {t('Registro di questa istanza')}
        </Button>
      </div>
      <div className="md-sezione md-stack">
        <div className="md-section-head">
          <h3 className="md-title-small" style={{ margin: 0 }}>{t('Log in tempo reale')}</h3>
          <Tabs value={sorgente} onValueChange={(v) => setSorgente(v as 'odoo' | 'nginx')} tabs={[{ value: 'odoo', label: 'Odoo' }, { value: 'nginx', label: 'nginx' }]} />
        </div>
        {log.error && <Banner tone="error" title={t('Ho perso il collegamento con il log')}>{t('Chiudi e riapri i dettagli per riprendere.')}</Banner>}
        {log.ended?.status === 'failed' && <Banner tone="error" title={t('Il log non si legge')}>{log.ended.error}</Banner>}
        <LogStream
          lines={log.lines}
          maxHeight="24rem"
          empty={aperto ? t('Mi collego al server: le righe compaiono qui appena arrivano.') : ''}
        />
      </div>
      {!ISTANZA_IN_CORSO.has(istanza.status) && (
        <div className="md-sezione">
          <ConPermesso ha={permessi} serve="instance.delete">
            <Button variant="text" icon={<Trash />} onClick={onElimina}>{t('Rimuovi l’istanza')}</Button>
          </ConPermesso>
        </div>
      )}
    </Details>
  );
}
